import { createFileRoute, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { DocumentsPage } from '../../../components/DocumentsPage'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'

export const Route = createFileRoute('/$org/documents/')({
    component: DocumentsList,
    // The explicit return type keeps `import_uid` OPTIONAL: every existing
    // navigate({ search: … }) call site across the app builds this object
    // literally, and a required key would force a mechanical edit in a dozen
    // unrelated files (spec 2026-07-27-10 §7).
    validateSearch: (search: Record<string, unknown>): {
        asset_uid: string | undefined
        // contract_uid filters on the document's HOME contract
        // (documents.contract_id, spec 2026-08-13-01 §5). Optional for the
        // same reason import_uid is: every existing navigate({ search: … })
        // call site builds this object literally.
        contract_uid?: string
        document_type_uid: string | undefined
        flags: string | undefined
        // Tri-state PII filter (spec 2026-08-13-02 §6). Optional for the same
        // reason as import_uid / contract_uid.
        pii?: 'true' | 'false' | 'unknown'
        import_uid?: string
        // Deposit-folder drill-down (spec 2026-08-16-07 §2): the import
        // dashboard's folder tree navigates here with the folder path.
        path_prefix?: string
        // Review-queue drill-down: documents with no document type yet.
        unclassified?: boolean
        to_validate?: boolean
        limit: number
    } => {
        const rawLimit = Number(search.limit)
        const limit = [20, 100, 200, 500].includes(rawLimit) ? rawLimit : 20
        return {
            asset_uid: search.asset_uid as string | undefined,
            contract_uid: search.contract_uid as string | undefined,
            document_type_uid: search.document_type_uid as string | undefined,
            flags: search.flags as string | undefined,
            // TanStack Router parses `?pii=true` into the BOOLEAN true, so the
            // raw value is normalized to a string before it is validated.
            pii: (['true', 'false', 'unknown'] as const).includes(String(search.pii) as 'true')
                ? (String(search.pii) as 'true' | 'false' | 'unknown')
                : undefined,
            // Import drill-down (spec 2026-07-27-10 §7): the dashboard's
            // document numbers navigate here.
            import_uid: search.import_uid as string | undefined,
            path_prefix: search.path_prefix as string | undefined,
            // TanStack Router parses `?unclassified=true` into a real boolean,
            // but a hand-typed URL still yields the string.
            unclassified: search.unclassified === true || search.unclassified === 'true'
                ? true
                : undefined,
            // The sidebar badge's list (spec 2026-10-27-23).
            to_validate: search.to_validate === true || search.to_validate === 'true' ? true : undefined,
            limit,
        }
    },
})

function DocumentsList() {
    const { org } = useParams({ from: '/$org/documents' })
    const { t } = useTranslation('documents')
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('breadcrumb.documents') }
    ])

    return <DocumentsPage />
}
